<template>
  <loader :is-loading="loading" />
  <div
    v-if="!isOpen"
    class="grid cursor-pointer grid-cols-15 items-center gap-x-3 px-4 py-[10px]"
    @click="openThread"
  >
    <div
      ref="avatarWithNameRef"
      v-tooltip="{
        content: log.sender?.name || log.from,
        onShow: () => isAvatarTextTruncated && (log.sender?.name || log.from),
      }"
      class="col-span-3 flex cursor-pointer items-center space-x-2"
    >
      <thread-icon :icon-info="iconInfo" />
      <email-status :email="log" />
      <email-avatar-with-name
        :text-class="textClass"
        :sender="log.sender?.name || log.from"
        class="w-4/5"
      />
    </div>
    <div class="flex items-center space-x-1">
      <total-indicator
        v-tooltip="$t('EMAIL_INTEGRATION.NUMBER_OF_EMAILS_IN_THREAD')"
        :value="log.communications_count"
      />
      <links-clicked-tooltip-icon
        v-if="!!log.links_clicked?.length"
        :links-clicked="log.links_clicked"
      />
    </div>
    <span
      ref="subjectRef"
      v-tooltip="{
        content: log.subject,
        onShow: () => isSubjectTextTruncated,
      }"
      data-cy="thread-subject"
      class="col-span-2 cursor-pointer truncate text-xs font-semibold text-headline"
    >
      {{ log.subject }}
    </span>
    <span
      ref="contentTextRef"
      v-tooltipable
      class="col-span-4 truncate text-xs text-headline"
      data-cy="thread-content-text"
    >
      {{ log.content_text }}
    </span>
    <business-name
      :id="log.latest_communication_email_id"
      :name="log.emailable?.business?.business_legal_name"
      :application-id="applicationId"
      :type="CommunicationType.email"
      class="col-span-2"
      @click.stop
    />
    <date-time
      v-if="log.created_at"
      ref="createdAtRef"
      :date="log.created_at"
      is-fixed
      show-time-if-today
      class="col-span-2 truncate text-xs"
    />
    <button
      class="flex h-6 w-6 items-center justify-center justify-self-end rounded-sm hover:bg-grey-hover"
    >
      <dropdown-chevron
        :is-open="isOpen"
        :is-default-position="false"
        class="cursor-pointer text-headline"
      />
    </button>
  </div>
  <!-- OPENED EMAIL THREAD -->
  <div v-else class="space-y-2">
    <div
      class="grid cursor-pointer grid-cols-5 items-center px-4 py-[10px]"
      @click="closeThread"
    >
      <div class="col-span-3 flex items-center space-x-3">
        <span class="truncate text-xs font-semibold text-headline">
          {{ log.subject }}
        </span>
        <div class="w-fit-content">
          <total-indicator
            v-if="log.communications_count > 1"
            :value="log.communications_count"
          />
        </div>
      </div>
      <div class="col-span-2 flex items-center space-x-3 justify-self-end">
        <business-name
          :id="log.latest_communication_email_id"
          :name="log.emailable?.business?.business_legal_name"
          :application-id="applicationId"
          class="w-full"
          :type="CommunicationType.email"
          @click.stop
        />
        <button
          class="flex h-6 w-6 items-center justify-center rounded-sm bg-primary-background-active"
        >
          <icon-chevron-up
            class="rotate-180 transform cursor-pointer text-primary transition-all"
            fill="#3769d6"
          />
        </button>
      </div>
    </div>
    <div class="max-h-80 space-y-2 overflow-y-auto px-4 pb-4">
      <div v-for="email in threadEmails" :key="email.id">
        <communication-log-email-thread-item
          :email="email"
          :business-name="log.emailable?.business?.business_legal_name"
          data-cy="thread-item"
          open
        />
      </div>
    </div>
    <div v-if="canLoadMore" class="flex w-full items-center justify-center">
      <button class="p-5" @click.stop.prevent="openThread">
        <span class="text-xs text-primary">
          {{ $t("COMMON.LOAD_MORE") }} {{ $t("COMMON.EMAIL", 2) }}
        </span>
      </button>
    </div>
    <div
      v-if="!threadEmails.length"
      class="flex items-center justify-center p-5 text-label"
    >
      {{ $t("COMMON.NO") }}
      {{ $t("COMMON.EMAIL", 2) }}
      {{ $t("COMMON.AVAILABLE") }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import { CommunicationType } from "@/enums/communicationLogs";
import { usePromiseWrapper } from "@/hooks/common";
import { useEmailLogInfo } from "@/hooks/communicationLogs";
import { useElementStatus } from "@/hooks/elements";
import emailService from "@/services/modules/emails";

import BusinessName from "@/components/communicationLogs/communication/emailThread/BusinessName.vue";
import CommunicationLogEmailThreadItem from "@/components/communicationLogs/communication/emailThread/CommunicationLogEmailThreadItem.vue";
import EmailAvatarWithName from "@/components/communicationLogs/communication/emailThread/EmailAvatarWithName.vue";
import EmailStatus from "@/components/communicationLogs/communication/emailThread/EmailStatus.vue";
import LinksClickedTooltipIcon from "@/components/communicationLogs/communication/emailThread/LinksClickedTooltipIcon.vue";
import ThreadIcon from "@/components/communicationLogs/communication/threads/ThreadIcon.vue";
import TotalIndicator from "@/components/deals/dataServices/cfa/moneythumb/TotalIndicator.vue";
import IconChevronUp from "@/components/icons/deals/IconChevronUp.vue";
import DateTime from "@/components/ui/ConvertibleDateTime.vue";
import DropdownChevron from "@/components/ui/DropdownChevron.vue";
import Loader from "@/components/ui/Loader.vue";

import type { IResponseMeta } from "@/models/common";
import type { EmailThread, EmailThreadItem } from "@/models/communicationLogs";

interface Props {
  log: EmailThread;
}

const props = defineProps<Props>();

const { isTextTruncated, isTextTruncatedOnResize } = useElementStatus();
const { iconInfo } = useEmailLogInfo(props.log);

const isOpen = ref(false);
const page = ref(0);
const openedThreadItemsMeta = ref<IResponseMeta | null>(null);

const avatarWithNameRef = ref<HTMLElement | null>(null);
const isAvatarTextTruncated = isTextTruncatedOnResize(avatarWithNameRef);
const subjectRef = ref<HTMLElement | null>(null);
const isSubjectTextTruncated = isTextTruncated(subjectRef);
const contentTextRef = ref<HTMLElement | null>(null);

const threadEmails = ref<EmailThreadItem[]>([]);

const textClass = computed(() => {
  return `text-label whitespace-nowrap ${
    isAvatarTextTruncated.value ? "overflow-hidden text-ellipsis" : ""
  }`;
});

const applicationId = computed(() =>
  props.log?.emailable?.application_id?.toString()
);

const canLoadMore = computed(() => {
  if (!openedThreadItemsMeta.value) {
    return false;
  }
  const currentTotal =
    openedThreadItemsMeta.value.current_page *
    openedThreadItemsMeta.value.per_page;
  const to = openedThreadItemsMeta.value?.to;
  return threadEmails.value.length && to && currentTotal <= to;
});

const { fetchWrapper: openThread, loading } = usePromiseWrapper(async () => {
  isOpen.value = true;
  const emailThreadItemsData = await emailService.getEmailThread(props.log.id);
  openedThreadItemsMeta.value = emailThreadItemsData.meta;

  threadEmails.value = threadEmails.value.concat(emailThreadItemsData.data);
  page.value++;
});

const closeThread = () => {
  isOpen.value = false;
  threadEmails.value = [];
  page.value = 0;
};
</script>
